import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { homeFor, useSession } from '../lib/session';
import { useAction, useQuery } from '../lib/useQuery';
import { Plain } from '../components/Shell';
import { Button, Card, ErrorNote, Field, Input, Loading } from '../components/ui';
import type { School, User } from '../lib/types';

/** 9i — sign in. */
export function SignIn() {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { adopt } = useSession();
  const googleDiv = useRef<HTMLDivElement>(null);
  const [ssoError, setSsoError] = useState<unknown>(null);

  /*
   * Google renders its own button into a div once its script loads. Both the script and the
   * button appear only when the server says a client id is configured — no id, no door.
   */
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const cfg = await api.get<{ googleClientId: string }>('/auth/sso/config');
        if (cancelled || !cfg.googleClientId || !googleDiv.current) return;
        await new Promise<void>((resolve, reject) => {
          if (document.getElementById('gsi-script')) return resolve();
          const el = document.createElement('script');
          el.id = 'gsi-script';
          el.src = 'https://accounts.google.com/gsi/client';
          el.onload = () => resolve();
          el.onerror = () => reject(new Error('Google sign-in did not load'));
          document.head.appendChild(el);
        });
        const gsi = (window as unknown as { google?: { accounts: { id: {
          initialize: (o: object) => void; renderButton: (el: HTMLElement, o: object) => void;
        } } } }).google;
        if (cancelled || !gsi || !googleDiv.current) return;
        gsi.accounts.id.initialize({
          client_id: cfg.googleClientId,
          callback: async (resp: { credential: string }) => {
            try {
              const r = await api.post<{ token: string; user: User; school: School }>('/auth/sso', {
                provider: 'google',
                idToken: resp.credential,
              });
              adopt(r.token, r.user, r.school);
              navigate(homeFor(r.user.role), { replace: true });
            } catch (err) {
              setSsoError(err);
            }
          },
        });
        gsi.accounts.id.renderButton(googleDiv.current, { theme: 'outline', size: 'large', width: 352 });
      } catch {
        /* No SSO configured, or the script is blocked — the password form stands alone. */
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { run, pending, error } = useAction(async () => {
    const user = await signIn(email, password);
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from && from !== '/signin' ? from : homeFor(user.role), { replace: true });
  });

  return (
    <Plain>
      <div style={{ maxWidth: 400, margin: '0 auto', padding: '96px 20px' }}>
        <Link to="/" style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--ink)' }}>SchoolVerse</Link>
        <h1 style={{ fontSize: 22, fontWeight: 600, margin: '22px 0 4px' }}>Sign in</h1>
        <p className="small muted" style={{ marginTop: 0 }}>Students joining a class only need the code on the board.</p>

        <Card>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void run();
            }}
          >
            <Field label="Email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" autoFocus />
            </Field>
            <Field label="Password">
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
            </Field>
            {error ? <ErrorNote error={error} /> : null}
            <Button variant="primary" size="lg" block type="submit" disabled={pending}>
              {pending ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </Card>

        <div ref={googleDiv} style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }} />
        {ssoError ? <div style={{ marginTop: 10 }}><ErrorNote error={ssoError} /></div> : null}

        <div className="row row--between small" style={{ marginTop: 16 }}>
          <Link to="/join">Join a class with a code</Link>
          <Link to="/forgot">Forgot password?</Link>
        </div>
        <div className="row row--between small" style={{ marginTop: 8 }}>
          <Link to="/signup/teacher">Teaching on your own? Start solo</Link>
          <Link to="/get-started">Set up a school</Link>
        </div>
      </div>
    </Plain>
  );
}

/** Accepting an invitation (13d, 9d). */
export function AcceptInvite() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { adopt } = useSession();
  const { data, loading, error } = useQuery<{
    email: string; role: string; school: { name: string; logoUrl?: string };
    name?: string | null; invitedBy?: string | null; classes?: { name: string; grade: string }[]; subjects?: string[]; expiresAt?: string;
  }>(`/auth/invite/${token}`);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  /* C.1: the roster's name is the default; the student can still correct it. */
  useEffect(() => {
    if (data?.name) setName(data.name);
  }, [data?.name]);
  const classes = data?.classes?.map((c) => c.name).join(', ');
  const days = data?.expiresAt ? Math.max(0, Math.ceil((new Date(data.expiresAt).getTime() - Date.now()) / 86_400_000)) : null;

  const accept = useAction(async () => {
    const res = await api.post<{ token: string; user: User; school: School }>('/auth/invite/accept', { token, name, password });
    adopt(res.token, res.user, res.school);
    navigate(res.user.role === 'student' ? '/student/first-run' : res.user.role === 'teacher' ? '/teacher/first-run' : '/admin/first-run');
  });

  return (
    <Plain>
      <div style={{ maxWidth: 400, margin: '0 auto', padding: '96px 20px' }}>
        {loading ? (
          <Loading />
        ) : error ? (
          <Card>
            <ErrorNote error={error} />
            <p className="small muted">Ask whoever invited you to send a new link.</p>
            <Link className="btn btn--secondary" to="/signin">Go to sign in</Link>
          </Card>
        ) : (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 600, margin: '0 0 4px' }}>{data?.role === 'student' ? 'Finish setting up' : `Join ${data?.school.name}`}</h1>
            {data?.role === 'student' ? (
              <>
                <p className="small muted" style={{ marginTop: 0 }}>
                  {data.invitedBy && classes ? `${data.invitedBy} added you to ${classes} at ${data.school.name}.` : `You have been added to ${data.school.name}.`}
                  {data.name ? ' Your name and class came from your teacher\u2019s roster.' : ''}
                </p>
                <p className="small muted">
                  This is a real account, not a one-off join code. Your work, marks and progress stay with you for the whole programme, across every class and teacher.
                </p>
              </>
            ) : (
              <>
                <p className="small muted" style={{ marginTop: 0 }}>
                  {data?.invitedBy ? `${data.invitedBy} invited you` : 'You have been invited'} as {data?.role === 'admin' ? 'an admin' : `a ${data?.role}`}, using {data?.email}.
                </p>
                {/* Teacher first login: what is already set up, so nothing is asked twice. */}
                {data?.subjects?.length ? <p className="small">Your subjects: <strong>{data.subjects.join(', ')}</strong>. Their thresholds and weighting reach your classes automatically.</p> : null}
                {classes ? <p className="small">Your classes are ready: <strong>{classes}</strong>.</p> : null}
              </>
            )}
            <Card>
              <form
                className="stack"
                onSubmit={(e) => {
                  e.preventDefault();
                  void accept.run();
                }}
              >
                <Field label="Your name" hint={data?.name && name === data.name ? 'From your school\u2019s roster' : undefined}>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} autoComplete="name" autoFocus />
                </Field>
                {data?.role === 'student' && (
                  <Field label="School email">
                    <Input value={data.email} readOnly aria-readonly="true" />
                  </Field>
                )}
                <Field label="Choose a password" hint="At least 8 characters.">
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" />
                </Field>
                {accept.error ? <ErrorNote error={accept.error} /> : null}
                <Button variant="primary" size="lg" block type="submit" disabled={accept.pending}>
                  {accept.pending ? 'Setting up…' : data?.role === 'student' ? 'Join the class' : 'Accept and continue'}
                </Button>
              </form>
            </Card>
            {data?.role === 'student' && (
              <>
                <p className="small" style={{ marginTop: 14 }}>
                  <strong>What your teacher can see.</strong> Your submitted work, marks and practice frequency. Not your private notes or your coaching chat transcripts.
                </p>
                <p className="small muted">Invited to {data.email}{days !== null ? ` · link expires in ${days} day${days === 1 ? '' : 's'}` : ''}</p>
              </>
            )}
          </>
        )}
      </div>
    </Plain>
  );
}

/**
 * 7d — the front door to a live event.
 *
 * Deliberately the largest, simplest form in the product: it is used by a student on a
 * phone, reading a code off a projector, often in the first minute of a lesson.
 */
